import { z } from 'zod';
import { ChatMessage } from './chat';
import { MONEY_NOTE_MAX, MoneyPurpose, Pence, TransferStatus } from './money';

// Money between players, sent or asked for in a direct chat (docs ECONOMY_LEDGER.md, section 5; Lagos Life's
// "💸 Send money"). Every transfer has a purpose; new lives wait before they can send; the admins set a fee,
// a limit for one transfer and for a London day; larger transfers wait for a check by the team.

const note = z
  .string()
  .trim()
  .max(MONEY_NOTE_MAX, `Keep the note to ${MONEY_NOTE_MAX} characters`)
  .transform((value) => value || undefined)
  .optional();

// send money to someone (the other person in a direct chat)
export const SendMoneyRequest = z
  .object({ lifeId: z.string().uuid(), amountPence: Pence, purpose: MoneyPurpose, note })
  .strict();
export type SendMoneyRequest = z.infer<typeof SendMoneyRequest>;
// ask someone for money; they pay it (or decline) from the card in the chat
export const AskMoneyRequest = SendMoneyRequest;
export type AskMoneyRequest = z.infer<typeof AskMoneyRequest>;
// buy someone a meal from the menu (delivered to their door)
export const BuyMealRequest = z.object({ lifeId: z.string().uuid(), mealId: z.string().min(1).max(40), note }).strict();
export type BuyMealRequest = z.infer<typeof BuyMealRequest>;
export const MoneyRequestAnswer = z.object({ answer: z.enum(['pay', 'decline', 'cancel']) }).strict();
export type MoneyRequestAnswer = z.infer<typeof MoneyRequestAnswer>;

// what the send sheet shows before sending: the wallet, the fee, what is left today, and any wait
export const MoneyAllowanceReply = z.object({
  walletPence: z.number().int(),
  // a new life can send from this moment; null when it already can
  sendFrom: z.string().nullable(),
  perTransferPence: z.number().int(),
  dailyLimitPence: z.number().int(),
  sentTodayPence: z.number().int(),
  // when today's limit starts again (London midnight)
  resetsAt: z.string(),
  feeBps: z.number().int(),
  feeMinPence: z.number().int(),
  // transfers above this wait for a check by the team
  reviewAbovePence: z.number().int(),
  // a request needs a chat you have both written in
  canAsk: z.boolean(),
});
export type MoneyAllowanceReply = z.infer<typeof MoneyAllowanceReply>;
export const MoneyMessageReply = z.object({ message: ChatMessage, walletPence: z.number().int() });
export type MoneyMessageReply = z.infer<typeof MoneyMessageReply>;

// ---- the admin's view: transfers between players, and the ones waiting for a check

const Party = z.object({
  lifeId: z.string().uuid(),
  userId: z.string().uuid(),
  firstName: z.string(),
  username: z.string().nullable(),
});
export const AdminTransferView = z.object({
  id: z.string().uuid(),
  from: Party,
  to: Party,
  amountPence: z.number().int(),
  feePence: z.number().int(),
  purpose: MoneyPurpose,
  note: z.string().nullable(),
  status: TransferStatus,
  // paying a request for money
  forRequest: z.boolean(),
  transactionId: z.string().uuid(),
  createdAt: z.string(),
  decidedAt: z.string().nullable(),
  decisionReason: z.string().nullable(),
});
export type AdminTransferView = z.infer<typeof AdminTransferView>;
export const AdminTransfersReply = z.object({
  held: z.array(AdminTransferView),
  recent: z.array(AdminTransferView),
});
export type AdminTransfersReply = z.infer<typeof AdminTransfersReply>;
export const TransferDecision = z
  .object({
    decision: z.enum(['send', 'return']),
    reason: z.string().trim().min(5, 'Give a reason of at least 5 characters').max(500),
  })
  .strict();
export type TransferDecision = z.infer<typeof TransferDecision>;
